import { UploadFileAPI } from '@brg/api'
import { BRGUploader } from '@brg/uploader'

export type TestImageUploadKind = 'question' | 'option'

/** Типы сущностей для UploadFileAPI — как у других блоков geography. */
const ENTITY: Record<TestImageUploadKind, string> = {
  question: 'testquestionimage',
  option: 'testansweroptionimage'
}

const LS_DEBUG_UPLOAD = 'brgDebugTestUpload'

function isTestUploadDebugEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    if (window.localStorage?.getItem(LS_DEBUG_UPLOAD) === '1') {
      return true
    }
  } catch {
    /* private mode */
  }
  try {
    return process.env.NODE_ENV === 'development'
  } catch {
    return false
  }
}

function logTestUpload(stage: string, detail?: unknown): void {
  if (!isTestUploadDebugEnabled()) {
    return
  }
  // eslint-disable-next-line no-console
  console.log(`[Test upload] ${stage}`, detail ?? '')
}

/**
 * UploadFileAPI.Upload типизирован как string, но с бэка иногда приходит объект / обёртка data.
 */
function normalizeFileContainerIdFromUploadResponse(
  raw: unknown
): string {
  if (typeof raw === 'string') {
    const s = raw.trim()
    return s
  }
  if (raw !== null && typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    const dataVal = o.data
    const nested =
      dataVal !== null && typeof dataVal === 'object' && !Array.isArray(dataVal)
        ? (dataVal as Record<string, unknown>)
        : null
    /** Иногда data приходит строкой-id */
    const dataAsString =
      typeof dataVal === 'string' && dataVal.trim() ? dataVal.trim() : ''
    const candidates: unknown[] = [
      dataAsString || undefined,
      o.fileContainerId,
      o.FileContainerId,
      o.containerId,
      o.ContainerId,
      o.fileId,
      o.FileId,
      o.id,
      o.Id,
      o.value,
      o.Value,
      o.result,
      o.uploadId,
      o.UploadId,
      nested?.fileContainerId,
      nested?.FileContainerId,
      nested?.fileId,
      nested?.FileId,
      nested?.id,
      nested?.Id
    ]
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) {
        return c.trim()
      }
    }
  }
  return ''
}

const ID_KEY_HINT = /container|fileid|file_id|upload|guid|resource|profile/i

function looksLikeFileContainerId(s: string): boolean {
  const t = s.trim()
  if (t.length < 16) {
    return false
  }
  return /^[0-9a-f-]+$/i.test(t)
}

/** Рекурсивно ищем строковый id, если контракт ответа нестандартный. */
function deepFindContainerId(raw: unknown, depth: number): string {
  if (depth <= 0 || raw == null) {
    return ''
  }
  if (typeof raw === 'string') {
    return looksLikeFileContainerId(raw) ? raw.trim() : ''
  }
  if (typeof raw !== 'object') {
    return ''
  }
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const f = deepFindContainerId(item, depth - 1)
      if (f) {
        return f
      }
    }
    return ''
  }
  const rec = raw as Record<string, unknown>
  const preferred = [
    'fileContainerId',
    'FileContainerId',
    'containerId',
    'ContainerId',
    'fileId',
    'FileId',
    'id',
    'Id',
    'value',
    'Value'
  ]
  for (const k of preferred) {
    const v = rec[k]
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  for (const [k, v] of Object.entries(rec)) {
    if (typeof v === 'string' && v.trim() && ID_KEY_HINT.test(k)) {
      return v.trim()
    }
  }
  for (const v of Object.values(rec)) {
    const f = deepFindContainerId(v, depth - 1)
    if (f) {
      return f
    }
  }
  return ''
}

function lastPathSegmentAsId(loc: string): string {
  const t = loc.trim()
  if (!t) {
    return ''
  }
  try {
    const u = new URL(t, 'https://placeholder.invalid')
    const segs = u.pathname.split('/').filter(Boolean)
    if (segs.length > 0) {
      const last = segs[segs.length - 1]
      if (last && looksLikeFileContainerId(last)) {
        return last
      }
    }
  } catch {
    /* relative Location */
  }
  const segs = t.split('/').filter(Boolean)
  if (segs.length > 0) {
    const last = segs[segs.length - 1]
    if (last && looksLikeFileContainerId(last)) {
      return last
    }
  }
  return ''
}

/** Id контейнера из заголовков (тело Upload часто пустое). */
function headerDerivedContainerId(res: Response): string {
  const explicit = [
    'X-File-Container-Id',
    'File-Container-Id',
    'X-Container-Id',
    'Container-Id',
    'X-File-Id',
    'File-Id'
  ]
  for (const n of explicit) {
    const v = res.headers.get(n)
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  for (const h of [
    'Location',
    'location',
    'Content-Location',
    'content-location'
  ]) {
    const loc = res.headers.get(h)
    if (!loc) {
      continue
    }
    const id = lastPathSegmentAsId(loc)
    if (id) {
      return id
    }
  }
  return ''
}

function logAllUploadResponseHeaders(res: Response): void {
  if (!isTestUploadDebugEnabled()) {
    return
  }
  const out: Record<string, string> = {}
  res.headers.forEach((v, k) => {
    out[k] = v
  })
  logTestUpload('UploadFileAPI.Upload all response headers', out)
}

type UnwrappedUploadBody = {
  parsed: unknown
  rawText: string
  headerId: string
}

/**
 * В браузере @brg/api для Upload нередко отдаёт сырой fetch Response (body не распарсен).
 */
async function unwrapUploadFileApiBody(raw: unknown): Promise<UnwrappedUploadBody> {
  if (typeof Response !== 'undefined' && raw instanceof Response) {
    const headerId = headerDerivedContainerId(raw)
    const text = await raw.text()
    if (!raw.ok) {
      throw new Error(
        `UploadFileAPI: HTTP ${raw.status} — ${text.slice(0, 300)}`
      )
    }
    if (!text.trim()) {
      return { parsed: null, rawText: '', headerId }
    }
    try {
      return {
        parsed: JSON.parse(text) as unknown,
        rawText: text,
        headerId
      }
    } catch {
      return { parsed: text, rawText: text, headerId }
    }
  }
  return { parsed: raw, rawText: '', headerId: '' }
}

/**
 * Загрузка файла через TUS → UploadFileAPI, возвращает идентификатор контейнера (строка от API).
 */
export function uploadTestBlockImage(
  file: File,
  options: {
    longreadId: string
    localization: string
    kind: TestImageUploadKind
  }
): Promise<string> {
  const { longreadId, localization, kind } = options
  return new Promise((resolve, reject) => {
    logTestUpload('start', {
      kind,
      longreadId,
      localization,
      fileName: file?.name,
      fileSize: file?.size,
      entityType: ENTITY[kind]
    })

    const input = document.createElement('input')
    input.type = 'file'
    /** Uppy file-input ждёт `input` как getter-функцию — см. useBRGUploader: create(() => ref.current, …) */
    const uploader = BRGUploader.create(() => input, (fileId) => {
      logTestUpload('TUS complete (fileId from uploader)', { fileId })
      if (!fileId) {
        reject(new Error('File upload failed'))
        return
      }
      const uploadArgs = {
        fileId,
        profileId: longreadId,
        service: 'geography',
        entityType: ENTITY[kind],
        localization
      }
      logTestUpload('UploadFileAPI.Upload request', uploadArgs)
      void UploadFileAPI.Upload(uploadArgs)
        .then(async (res) => {
          logTestUpload('UploadFileAPI.Upload raw response', res)
          logAllUploadResponseHeaders(res)
          let unwrapped: UnwrappedUploadBody
          try {
            unwrapped = await unwrapUploadFileApiBody(res)
          } catch (e) {
            logTestUpload('UploadFileAPI.Upload unwrap error', e)
            reject(e instanceof Error ? e : new Error(String(e)))
            return
          }
          const { parsed, rawText, headerId } = unwrapped
          logTestUpload('UploadFileAPI.Upload response text (preview)', {
            length: rawText.length,
            preview: rawText.slice(0, 400)
          })
          logTestUpload('UploadFileAPI.Upload parsed body', parsed)
          logTestUpload(
            'UploadFileAPI.Upload id from headers (Location / X-…)',
            headerId || '(none)'
          )

          let containerId =
            (headerId && headerId.trim()) ||
            normalizeFileContainerIdFromUploadResponse(parsed) ||
            deepFindContainerId(parsed, 5)

          /**
           * Пустое тело + нет Location: часто fileId из TUS совпадает с контейнером для geograffee.
           * Если тесты на бэке ждут другой id — смотрите «all response headers» в логе.
           */
          if (!containerId && fileId?.trim()) {
            containerId = fileId.trim()
            logTestUpload(
              'fileContainerId fallback: TUS fileId (нет тела и подходящих заголовков)',
              containerId
            )
          }

          if (!containerId) {
            reject(
              new Error(
                `UploadFileAPI: не удалось получить fileContainerId. Тело: ${rawText.slice(0, 200) || '(пусто)'}`
              )
            )
            return
          }
          logTestUpload('normalized fileContainerId', containerId)
          resolve(containerId)
        })
        .catch((err) => {
          logTestUpload('UploadFileAPI.Upload error', err)
          reject(err)
        })
    })
    /**
     * По умолчанию @uppy/tus с resume пытается продолжить прошлую сессию (findPreviousUploads → HEAD по URL).
     * Для несуществующего на сервере upload-id это даёт 404 в консоли и иногда срывает новую загрузку.
     */
    const tusPlugin = uploader.uppy.getPlugin('Tus') as
      | { setOptions(opts: { resume?: boolean }): void }
      | undefined
    tusPlugin?.setOptions({ resume: false })
    uploader.upload(file)
  })
}
import { UploadFileAPI } from '@brg/api'
import { BRGUploader } from '@brg/uploader'

export type TestImageUploadKind = 'question' | 'option'

/** Типы сущностей для UploadFileAPI — как у других блоков geography. */
const ENTITY: Record<TestImageUploadKind, string> = {
  question: 'testquestionimage',
  option: 'testansweroptionimage'
}

const LS_DEBUG_UPLOAD = 'brgDebugTestUpload'

function isTestUploadDebugEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    if (window.localStorage?.getItem(LS_DEBUG_UPLOAD) === '1') {
      return true
    }
  } catch {
    /* private mode */
  }
  try {
    return process.env.NODE_ENV === 'development'
  } catch {
    return false
  }
}

function logTestUpload(stage: string, detail?: unknown): void {
  if (!isTestUploadDebugEnabled()) {
    return
  }
  // eslint-disable-next-line no-console
  console.log(`[Test upload] ${stage}`, detail ?? '')
}

/**
 * UploadFileAPI.Upload типизирован как string, но с бэка иногда приходит объект / обёртка data.
 */
function normalizeFileContainerIdFromUploadResponse(
  raw: unknown
): string {
  if (typeof raw === 'string') {
    const s = raw.trim()
    return s
  }
  if (raw !== null && typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    const dataVal = o.data
    const nested =
      dataVal !== null && typeof dataVal === 'object' && !Array.isArray(dataVal)
        ? (dataVal as Record<string, unknown>)
        : null
    /** Иногда data приходит строкой-id */
    const dataAsString =
      typeof dataVal === 'string' && dataVal.trim() ? dataVal.trim() : ''
    const candidates: unknown[] = [
      dataAsString || undefined,
      o.fileContainerId,
      o.FileContainerId,
      o.containerId,
      o.ContainerId,
      o.fileId,
      o.FileId,
      o.id,
      o.Id,
      o.value,
      o.Value,
      o.result,
      o.uploadId,
      o.UploadId,
      nested?.fileContainerId,
      nested?.FileContainerId,
      nested?.fileId,
      nested?.FileId,
      nested?.id,
      nested?.Id
    ]
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) {
        return c.trim()
      }
    }
  }
  return ''
}

const ID_KEY_HINT = /container|fileid|file_id|upload|guid|resource|profile/i

function looksLikeFileContainerId(s: string): boolean {
  const t = s.trim()
  if (t.length < 16) {
    return false
  }
  return /^[0-9a-f-]+$/i.test(t)
}

/** Рекурсивно ищем строковый id, если контракт ответа нестандартный. */
function deepFindContainerId(raw: unknown, depth: number): string {
  if (depth <= 0 || raw == null) {
    return ''
  }
  if (typeof raw === 'string') {
    return looksLikeFileContainerId(raw) ? raw.trim() : ''
  }
  if (typeof raw !== 'object') {
    return ''
  }
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const f = deepFindContainerId(item, depth - 1)
      if (f) {
        return f
      }
    }
    return ''
  }
  const rec = raw as Record<string, unknown>
  const preferred = [
    'fileContainerId',
    'FileContainerId',
    'containerId',
    'ContainerId',
    'fileId',
    'FileId',
    'id',
    'Id',
    'value',
    'Value'
  ]
  for (const k of preferred) {
    const v = rec[k]
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  for (const [k, v] of Object.entries(rec)) {
    if (typeof v === 'string' && v.trim() && ID_KEY_HINT.test(k)) {
      return v.trim()
    }
  }
  for (const v of Object.values(rec)) {
    const f = deepFindContainerId(v, depth - 1)
    if (f) {
      return f
    }
  }
  return ''
}

function lastPathSegmentAsId(loc: string): string {
  const t = loc.trim()
  if (!t) {
    return ''
  }
  try {
    const u = new URL(t, 'https://placeholder.invalid')
    const segs = u.pathname.split('/').filter(Boolean)
    if (segs.length > 0) {
      const last = segs[segs.length - 1]
      if (last && looksLikeFileContainerId(last)) {
        return last
      }
    }
  } catch {
    /* relative Location */
  }
  const segs = t.split('/').filter(Boolean)
  if (segs.length > 0) {
    const last = segs[segs.length - 1]
    if (last && looksLikeFileContainerId(last)) {
      return last
    }
  }
  return ''
}

/** Id контейнера из заголовков (тело Upload часто пустое). */
function headerDerivedContainerId(res: Response): string {
  const explicit = [
    'X-File-Container-Id',
    'File-Container-Id',
    'X-Container-Id',
    'Container-Id',
    'X-File-Id',
    'File-Id'
  ]
  for (const n of explicit) {
    const v = res.headers.get(n)
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  for (const h of [
    'Location',
    'location',
    'Content-Location',
    'content-location'
  ]) {
    const loc = res.headers.get(h)
    if (!loc) {
      continue
    }
    const id = lastPathSegmentAsId(loc)
    if (id) {
      return id
    }
  }
  return ''
}

function logAllUploadResponseHeaders(res: Response): void {
  if (!isTestUploadDebugEnabled()) {
    return
  }
  const out: Record<string, string> = {}
  res.headers.forEach((v, k) => {
    out[k] = v
  })
  logTestUpload('UploadFileAPI.Upload all response headers', out)
}

type UnwrappedUploadBody = {
  parsed: unknown
  rawText: string
  headerId: string
}

/**
 * В браузере @brg/api для Upload нередко отдаёт сырой fetch Response (body не распарсен).
 */
async function unwrapUploadFileApiBody(raw: unknown): Promise<UnwrappedUploadBody> {
  if (typeof Response !== 'undefined' && raw instanceof Response) {
    const headerId = headerDerivedContainerId(raw)
    const text = await raw.text()
    if (!raw.ok) {
      throw new Error(
        `UploadFileAPI: HTTP ${raw.status} — ${text.slice(0, 300)}`
      )
    }
    if (!text.trim()) {
      return { parsed: null, rawText: '', headerId }
    }
    try {
      return {
        parsed: JSON.parse(text) as unknown,
        rawText: text,
        headerId
      }
    } catch {
      return { parsed: text, rawText: text, headerId }
    }
  }
  return { parsed: raw, rawText: '', headerId: '' }
}

/**
 * Загрузка файла через TUS → UploadFileAPI, возвращает идентификатор контейнера (строка от API).
 */
export function uploadTestBlockImage(
  file: File,
  options: {
    longreadId: string
    localization: string
    kind: TestImageUploadKind
  }
): Promise<string> {
  const { longreadId, localization, kind } = options
  return new Promise((resolve, reject) => {
    logTestUpload('start', {
      kind,
      longreadId,
      localization,
      fileName: file?.name,
      fileSize: file?.size,
      entityType: ENTITY[kind]
    })

    const input = document.createElement('input')
    input.type = 'file'
    /** Uppy file-input ждёт `input` как getter-функцию — см. useBRGUploader: create(() => ref.current, …) */
    const uploader = BRGUploader.create(() => input, (fileId) => {
      logTestUpload('TUS complete (fileId from uploader)', { fileId })
      if (!fileId) {
        reject(new Error('File upload failed'))
        return
      }
      const uploadArgs = {
        fileId,
        profileId: longreadId,
        service: 'geography',
        entityType: ENTITY[kind],
        localization
      }
      logTestUpload('UploadFileAPI.Upload request', uploadArgs)
      void UploadFileAPI.Upload(uploadArgs)
        .then(async (res) => {
          logTestUpload('UploadFileAPI.Upload raw response', res)
          logAllUploadResponseHeaders(res)
          let unwrapped: UnwrappedUploadBody
          try {
            unwrapped = await unwrapUploadFileApiBody(res)
          } catch (e) {
            logTestUpload('UploadFileAPI.Upload unwrap error', e)
            reject(e instanceof Error ? e : new Error(String(e)))
            return
          }
          const { parsed, rawText, headerId } = unwrapped
          logTestUpload('UploadFileAPI.Upload response text (preview)', {
            length: rawText.length,
            preview: rawText.slice(0, 400)
          })
          logTestUpload('UploadFileAPI.Upload parsed body', parsed)
          logTestUpload(
            'UploadFileAPI.Upload id from headers (Location / X-…)',
            headerId || '(none)'
          )

          let containerId =
            (headerId && headerId.trim()) ||
            normalizeFileContainerIdFromUploadResponse(parsed) ||
            deepFindContainerId(parsed, 5)

          /**
           * Пустое тело + нет Location: часто fileId из TUS совпадает с контейнером для geograffee.
           * Если тесты на бэке ждут другой id — смотрите «all response headers» в логе.
           */
          if (!containerId && fileId?.trim()) {
            containerId = fileId.trim()
            logTestUpload(
              'fileContainerId fallback: TUS fileId (нет тела и подходящих заголовков)',
              containerId
            )
          }

          if (!containerId) {
            reject(
              new Error(
                `UploadFileAPI: не удалось получить fileContainerId. Тело: ${rawText.slice(0, 200) || '(пусто)'}`
              )
            )
            return
          }
          logTestUpload('normalized fileContainerId', containerId)
          resolve(containerId)
        })
        .catch((err) => {
          logTestUpload('UploadFileAPI.Upload error', err)
          reject(err)
        })
    })
    /**
     * По умолчанию @uppy/tus с resume пытается продолжить прошлую сессию (findPreviousUploads → HEAD по URL).
     * Для несуществующего на сервере upload-id это даёт 404 в консоли и иногда срывает новую загрузку.
     */
    const tusPlugin = uploader.uppy.getPlugin('Tus') as
      | { setOptions(opts: { resume?: boolean }): void }
      | undefined
    tusPlugin?.setOptions({ resume: false })
    uploader.upload(file)
  })
}
